import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/lib/store/authStore'
import {
  cpGetLineCampaignSegment,
  cpGetLineCampaignFilterOptions,
  cpSendLineCampaign,
  cpGetLineCampaignHistory,
  cpGetLineCampaignRecipients,
  cpGetLineCampaignVisits,
  type LineCampaignSegment,
  type LineCampaignSegmentResponse,
  type LineCampaignSendResponse,
  type LineCampaignFilterCondition,
  type LineCampaignFilterOptions,
  type LineCampaignHistoryResponse,
  type LineCampaignRecipientsResponse,
  type LineCampaignVisitsResponse,
  type CampaignMedia,
} from '@/lib/api/controlPlaneClient'

/**
 * Campaign is action-driven (preview a segment, then send), so both operations
 * are mutations rather than always-on queries — nothing runs until the user asks.
 */
export function useLineCampaign() {
  const previewMutation = useMutation<
    LineCampaignSegmentResponse,
    Error,
    {
      segment: LineCampaignSegment
      minOrders?: number
      minSpend?: number
      filters?: LineCampaignFilterCondition[]
    }
  >({
    mutationFn: (params) => cpGetLineCampaignSegment(params),
  })

  const sendMutation = useMutation<
    LineCampaignSendResponse,
    Error,
    {
      lineUserIds: string[]
      textContent?: string
      includePaymentLink?: boolean
      messageType?: 'text' | 'rich' | 'video'
      media?: CampaignMedia
      couponCode?: string
    }
  >({
    mutationFn: (params) => cpSendLineCampaign(params),
  })

  return {
    preview: previewMutation.mutate,
    previewAsync: previewMutation.mutateAsync,
    previewData: previewMutation.data ?? null,
    isPreviewing: previewMutation.isPending,
    previewError: previewMutation.error?.message ?? null,
    resetPreview: previewMutation.reset,

    send: sendMutation.mutate,
    sendResult: sendMutation.data ?? null,
    isSending: sendMutation.isPending,
    sendError: sendMutation.error?.message ?? null,
    resetSend: sendMutation.reset,
  }
}

/**
 * The pickable collections + products for the advanced audience builder.
 * Scoped per active store (re-fetches when the store switches) and cached —
 * the option list only changes when the tenant sells something new.
 */
export function useLineCampaignFilterOptions(enabled = true) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery<LineCampaignFilterOptions>({
    queryKey: ['line-campaign-filter-options', activeStorefrontKey],
    queryFn: () => cpGetLineCampaignFilterOptions(),
    enabled,
    staleTime: 10 * 60 * 1000,
  })
}

/**
 * Past TARGETED campaign sends for the active store. Keyed by the active
 * storefront so switching stores refetches the right store's history.
 */
export function useLineCampaignHistory(enabled = true) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery<LineCampaignHistoryResponse>({
    queryKey: ['line-campaign-history', activeStorefrontKey],
    queryFn: () => cpGetLineCampaignHistory(50),
    enabled,
    staleTime: 60 * 1000,
  })
}

/**
 * The recipients (names + blocked status) of ONE past campaign send. Lazy —
 * only fetched when a history row is expanded (enabled=true). The block-check
 * hits LINE per recipient, so results are cached briefly and never refetched
 * on window focus.
 */
export function useLineCampaignRecipients(broadcastLogName: string, enabled: boolean) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery<LineCampaignRecipientsResponse>({
    queryKey: ['line-campaign-recipients', activeStorefrontKey, broadcastLogName],
    queryFn: () => cpGetLineCampaignRecipients(broadcastLogName),
    enabled: enabled && !!broadcastLogName,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * Visit attribution for ONE past campaign send (how many recipients visited the
 * store after the send, via the campaign's utm-tagged links). Lazy — only fetched
 * when a history row is expanded. Attribution is future-only + link-required, so
 * older sends legitimately return 0; the UI handles 0 / -1 (unavailable)
 * gracefully. Cached briefly and not refetched on focus.
 */
export function useLineCampaignVisits(broadcastLogName: string, enabled: boolean) {
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  return useQuery<LineCampaignVisitsResponse>({
    queryKey: ['line-campaign-visits', activeStorefrontKey, broadcastLogName],
    queryFn: () => cpGetLineCampaignVisits(broadcastLogName),
    enabled: enabled && !!broadcastLogName,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
